import React, { useState, useEffect } from 'react';
import { Search, User, Sun, Moon, Menu, Shield } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import NotificationPanel from './NotificationPanel';
import { Link } from 'react-router-dom';

const Header = () => {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check for saved preference or system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  // Effect to handle scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Effect to apply dark mode
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.body.classList.remove('dark-mode');
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);
  
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  
  return (
    <header className={`bg-gray-800 text-white py-3 px-4 sticky top-0 z-30 transition-all duration-300 ${scrolled ? 'shadow-lg' : ''}`}>
      <div className="flex justify-between items-center">
        <div className="md:hidden">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('maphra:toggle-navigation'))}
            className="p-1 rounded-full hover:bg-gray-700 transition-colors"
            aria-label="Open navigation"
            aria-controls="maphra-primary-navigation"
            data-testid="mobile-navigation-toggle"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>

        <div className="text-lg sm:text-xl font-semibold flex items-center">
          <Shield className="h-7 w-7 mr-2 text-blue-400" aria-hidden="true" />
          <span className="bg-gradient-to-r from-blue-400 to-cyan-300 text-transparent bg-clip-text">
            Maphra
          </span>
          <span className="ml-2 hidden opacity-80 sm:inline">by Dedsec<sup className="ml-0.5 text-xs">™</sup></span>
        </div>
        
        <div className="flex items-center space-x-2 md:space-x-4">
          <div className={`relative transition-all duration-300 ${showSearch ? 'w-48 md:w-64' : 'w-8'}`}>
            <button 
              onClick={() => setShowSearch(!showSearch)}
              className={`absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors z-10 ${showSearch ? 'opacity-100' : 'opacity-80'}`}
            >
              <Search className="h-4 w-4" />
            </button>
            
            <input
              type="text"
              placeholder="Search..."
              className={`bg-gray-700 rounded-full py-1.5 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                showSearch ? 'opacity-100 pl-9 pr-4 w-full' : 'opacity-0 w-8 cursor-pointer'
              }`}
              onBlur={() => {
                if (!showSearch) return;
                setTimeout(() => setShowSearch(false), 200);
              }}
            />
          </div>
          
          <div className="flex items-center space-x-1 md:space-x-3">
            <button 
              onClick={toggleDarkMode}
              className="p-1.5 rounded-full hover:bg-gray-700 transition-colors relative group"
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? (
                <Sun className="h-4 w-4 text-yellow-400" />
              ) : (
                <Moon className="h-4 w-4 text-blue-300" />
              )}
              <span className="absolute -bottom-9 left-50 transform -translate-x-1/2 bg-gray-900 text-xs text-white py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                {isDarkMode ? 'Light mode' : 'Dark mode'}
              </span>
            </button>
            
            <NotificationPanel />
            
            <Link 
              to="/profile" 
              className="flex items-center space-x-2 hover:text-blue-300 transition-colors duration-200 relative group ml-1"
            >
              <div className="flex items-center">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt="Profile"
                    className="h-7 w-7 rounded-full ring-2 ring-blue-500 object-cover"
                  />
                ) : (
                  <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full p-0.5">
                    <div className="bg-gray-800 rounded-full p-1">
                      <User className="h-4 w-4 text-blue-300" />
                    </div>
                  </div>
                )}
                <span className="text-sm font-medium ml-2 hidden md:inline">{user?.username}</span>
              </div>
              <span className="absolute -bottom-9 left-0 bg-gray-900 text-xs text-white py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                View profile
              </span>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
